import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Lock, Unlock, RotateCcw, X, Palette, BookOpen } from 'lucide-react';
import { useDraggable } from '../hooks/useDraggable';
import PBRBrowser from './PBRBrowser';
import type { PbrMaps } from '../stores/worldStore';
import './GroundControls.css';

// PBR texture presets — each folder in public/textures/ must have color.jpg, normal.jpg, roughness.jpg, ao.jpg
const TEXTURE_PRESETS = [
  { id: 'water', label: 'Water' },
  { id: 'water2', label: 'Water 2' },
  { id: 'grass', label: 'Grass' },
  { id: 'dirt', label: 'Dirt' },
  { id: 'sand', label: 'Sand' },
  { id: 'wood1', label: 'Wood' },
  { id: 'wood2', label: 'Wood 2' },
  { id: 'wood3', label: 'Wood 3' },
  { id: 'wood4', label: 'Wood 4' },
  { id: 'tile1', label: 'Tile' },
  { id: 'tile2', label: 'Tile 2' },
  { id: 'tile3', label: 'Tile 3' },
  { id: 'marble', label: 'Marble' },
  { id: 'brick', label: 'Brick' },
  { id: 'cobblestone', label: 'Cobble' },
  { id: 'paving1', label: 'Paving' },
  { id: 'paving2', label: 'Paving 2' },
];

interface GroundControlsProps {
  size: number;
  color: string;
  textureUrl: string | null;
  texturePreset: string | null;
  textureScale: number;
  opacity: number;
  groundVisible: boolean;
  showGrid: boolean;
  isLocked: boolean;
  onSizeChange: (size: number) => void;
  onColorChange: (color: string) => void;
  onTextureChange: (url: string | null) => void;
  onPresetChange: (preset: string | null) => void;
  onTextureScaleChange: (scale: number) => void;
  onOpacityChange: (opacity: number) => void;
  onGroundVisibleToggle: () => void;
  onGridToggle: () => void;
  onLockToggle: () => void;
  onReset: () => void;
  onClose: () => void;
  onUploadTexture: () => void;
  pbrMaps?: PbrMaps | null;
  onPbrMapsChange?: (maps: PbrMaps | null) => void;
}

const GroundControls: React.FC<GroundControlsProps> = ({
  size,
  color,
  textureUrl,
  texturePreset,
  textureScale,
  opacity,
  groundVisible,
  showGrid,
  isLocked,
  onSizeChange,
  onColorChange,
  onTextureChange,
  onPresetChange,
  onTextureScaleChange,
  onOpacityChange,
  onGroundVisibleToggle,
  onGridToggle,
  onLockToggle,
  onReset,
  pbrMaps,
  onPbrMapsChange,
  onClose,
  onUploadTexture
}) => {
  const [showPBRBrowser, setShowPBRBrowser] = useState(false);
  const { dragStyle, onPointerDown: onDragPointerDown } = useDraggable();
  return (
    <>
    <div className="ground-controls glassmorphism-dark" style={dragStyle} onPointerDown={(e) => { e.stopPropagation(); onDragPointerDown(e); }} onMouseDown={(e) => e.stopPropagation()}>
      <div className="controls-header" data-drag-handle>
        <h3>Ground Controls</h3>
        <div className="header-buttons">
          <button
            className={`icon-btn ${isLocked ? 'locked' : ''}`}
            onClick={onLockToggle}
            title={isLocked ? 'Unlock controls' : 'Lock controls'}
          >
            {isLocked ? <Lock size={16} /> : <Unlock size={16} />}
          </button>
          <button
            className="icon-btn"
            onClick={onClose}
            title="Close controls"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      <div className={`controls-content ${isLocked ? 'disabled' : ''}`}>
        <div className="control-group">
          <label>Ground Plane</label>
          <div className="control-row">
            <span>Show:</span>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={groundVisible}
                onChange={onGroundVisibleToggle}
                disabled={isLocked}
              />
              <span className="toggle-slider"></span>
            </label>
            <span className="value">{groundVisible ? 'On' : 'Off'}</span>
          </div>
          {!groundVisible && (
            <div style={{ fontSize: 11, opacity: 0.6, marginTop: 4 }}>
              No ground at all — nothing to stand on. Use Opacity instead if you
              want it invisible but still walkable.
            </div>
          )}
        </div>

        <div className="control-group">
          <label>Size</label>
          <div className="control-row">
            <span>Scale:</span>
            <input
              type="range"
              min="10"
              max="500"
              step="10"
              value={size}
              onChange={(e) => onSizeChange(parseFloat(e.target.value))}
              disabled={isLocked}
            />
            <span className="value">{size}</span>
          </div>
        </div>

        <div className="control-group">
          <label>Opacity</label>
          <div className="control-row">
            <span>Fade:</span>
            <input
              type="range"
              min="0"
              max="100"
              step="1"
              value={Math.round(opacity * 100)}
              onChange={(e) => onOpacityChange(parseFloat(e.target.value) / 100)}
              disabled={isLocked}
            />
            <span className="value">{Math.round(opacity * 100)}%</span>
          </div>
          {opacity === 0 && (
            <div style={{ fontSize: 11, opacity: 0.6, marginTop: 4 }}>
              Hidden, but still solid — you can walk on it and place objects.
            </div>
          )}
        </div>

        <div className="control-group">
          <label>Color</label>
          <div className="control-row">
            <span>Color:</span>
            <input
              type="color"
              value={color}
              onChange={(e) => onColorChange(e.target.value)}
              disabled={isLocked}
              className="color-input"
            />
            <span className="value">{color}</span>
          </div>
        </div>

        <div className="control-group">
          <label>Grid</label>
          <div className="control-row">
            <span>Show:</span>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={showGrid}
                onChange={onGridToggle}
                disabled={isLocked}
              />
              <span className="toggle-slider"></span>
            </label>
            <span className="value">{showGrid ? 'On' : 'Off'}</span>
          </div>
        </div>

        <div className="control-group">
          <label>Material (PBR)</label>
          <div className="texture-preset-grid">
            <button
              className={`texture-preset-btn ${!texturePreset && !textureUrl ? 'active' : ''}`}
              onClick={() => { onPresetChange(null); onTextureChange(null); }}
              disabled={isLocked}
              title="No texture"
            >
              <div className="preset-swatch" style={{ background: color }} />
              <span>None</span>
            </button>
            {TEXTURE_PRESETS.map((p) => (
              <button
                key={p.id}
                className={`texture-preset-btn ${texturePreset === p.id ? 'active' : ''}`}
                onClick={() => onPresetChange(p.id)}
                disabled={isLocked}
                title={p.label}
              >
                <img src={`/textures/${p.id}/color.jpg`} alt={p.label} className="preset-thumb" />
                <span>{p.label}</span>
              </button>
            ))}
          </div>
          {onPbrMapsChange && (
            <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
              <button
                className="upload-texture-btn"
                style={{ flex: 1 }}
                onClick={() => setShowPBRBrowser(true)}
                disabled={isLocked}
                title="Browse thousands of PBR materials"
              >
                <BookOpen size={14} /> {pbrMaps ? 'Change Library Material' : 'Browse Material Library'}
              </button>
              {pbrMaps && (
                <button
                  className="remove-texture-btn"
                  onClick={() => onPbrMapsChange(null)}
                  disabled={isLocked}
                  title="Remove library material"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          )}
        </div>

        {(texturePreset || textureUrl || pbrMaps) && (
          <div className="control-group">
            <label>Pattern Scale</label>
            <div className="control-row">
              <span>Tile:</span>
              <input
                type="range"
                min="1"
                max="30"
                step="0.5"
                value={textureScale}
                onChange={(e) => onTextureScaleChange(parseFloat(e.target.value))}
                disabled={isLocked}
              />
              <span className="value">{textureScale}</span>
            </div>
          </div>
        )}

        <div className="control-group">
          <label>Custom Texture</label>
          <div className="texture-controls">
            {textureUrl ? (
              <div className="texture-preview">
                <img src={textureUrl} crossOrigin="anonymous" alt="Ground texture" />
                <button
                  className="remove-texture-btn"
                  onClick={() => onTextureChange(null)}
                  disabled={isLocked}
                  title="Remove texture"
                >
                  <X size={14} />
                </button>
              </div>
            ) : (
              <button
                className="upload-texture-btn"
                onClick={() => { onPresetChange(null); onUploadTexture(); }}
                disabled={isLocked}
              >
                <Palette size={16} />
                <span>Upload Texture</span>
              </button>
            )}
          </div>
        </div>

        <button
          className="reset-btn"
          onClick={onReset}
          disabled={isLocked}
        >
          <RotateCcw size={16} />
          Reset Ground
        </button>
      </div>
    </div>
    {showPBRBrowser && onPbrMapsChange && createPortal(
      <PBRBrowser
        onApply={(maps) => {
          onPbrMapsChange(maps);
          setShowPBRBrowser(false);
        }}
        onClose={() => setShowPBRBrowser(false)}
      />,
      document.body
    )}
    </>
  );
};

export default GroundControls;
